import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Badge, Button, FileInput, FilterChip, Input, Segmented, Skeleton, StatusHint,
} from '@indipia/ui-core';
import {
  fetchSite,
  fetchFloorPlanThumbnails,
  fetchThumbnailObjectUrl,
  fetchBuildings,
  createBuilding,
  convertFloorPlan,
  saveFloorPlan,
  updateSite,
  updateSiteSize,
} from './api';
import type { FloorPlanThumbnailItem, BuildingItem, StepBResult } from './types';

/** 썸네일 이미지 — API 클라이언트 경유 blob 조회 (nginx 직접 접근 방지, 모바일 동일 방식) */
function ThumbnailImage({ thumbnailUrl, alt, className }: { thumbnailUrl: string; alt: string; className?: string }) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!thumbnailUrl) return;
    let revoked = false;
    fetchThumbnailObjectUrl(thumbnailUrl)
      .then((url) => { if (!revoked) setObjectUrl(url); })
      .catch(() => {});
    return () => {
      revoked = true;
      setObjectUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
    };
  }, [thumbnailUrl]);

  if (!objectUrl) {
    return (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" className="text-text-disabled">
        <rect x="3" y="3" width="18" height="18" rx="2"/>
        <path d="M3 9h18M9 21V9M3 15h6"/>
      </svg>
    );
  }
  return <img src={objectUrl} alt={alt} className={className} />;
}

interface StepCProps {
  stepBResult: StepBResult;
  onPrev: () => void;
  onComplete: (siteId: number) => void;
  onClose: () => void;
}

function parseSizeLabel(label: string | null): { pyeong: number | null; squareMeters: number | null } {
  if (!label) return { pyeong: null, squareMeters: null };
  const pyeongMatch = label.match(/(\d+)\s*평/);
  const sqMatch = label.match(/(\d+)\s*㎡/);
  return {
    pyeong: pyeongMatch ? parseInt(pyeongMatch[1], 10) : null,
    squareMeters: sqMatch ? parseInt(sqMatch[1], 10) : null,
  };
}

const SQ_PER_PYEONG = 3.3058;

/**
 * Step C — 도면 등록 (정의서 E-5)
 *
 * 평형 필드 분기:
 *   - 단지 매핑 완료(bldgId 확정): 읽기전용 표시
 *   - 단지 미매핑 + buildings 있음: 칩 + 직접 입력 (StepB 동일 UI)
 *   - 단지 미매핑 + buildings 없음: 직접 입력만
 *
 * 저장 흐름:
 *   1. 단지 미매핑 + 칩 선택: PATCH /customer/sites/{siteId}/size
 *   2. 단지 미매핑 + 직접 입력: POST /indipia/buildings → PATCH /size
 *   3. 타입 선택: PATCH /customer/sites/{siteId} (floorPlanId)
 *   4. 이미지 업로드: POST conversions/vulcan → POST plans → PATCH sites/{siteId}
 *   5. 도면 없이 완료: onComplete 직접 호출
 */
export function StepC({ stepBResult, onPrev, onComplete }: StepCProps) {
  const { siteId, bldgId: stepBBldgId, roadAddress } = stepBResult;

  // 평형 — 칩 선택 (단지 미매핑)
  const [selectedBldg, setSelectedBldg] = useState<BuildingItem | null>(null);
  // 평형 — 직접 입력
  const [pyeongDirectInput, setPyeongDirectInput] = useState(false);
  const [pyeongValue, setPyeongValue] = useState('');
  const [isPyeong, setIsPyeong] = useState(true); // true=평, false=㎡

  // 도면 타입 선택
  const [selectedPlan, setSelectedPlan] = useState<FloorPlanThumbnailItem | null>(null);
  const autoSelectedRef = useRef(false);

  // 도면 업로드
  const [uploadPreviewUrl, setUploadPreviewUrl] = useState<string | null>(null);
  const [convertedPlanJson, setConvertedPlanJson] = useState<object | null>(null);

  // E-5: 화면 진입 시 현장 상세 조회
  const { data: siteDetail, isLoading: isSiteLoading } = useQuery({
    queryKey: ['site', siteId],
    queryFn: () => fetchSite(siteId),
    staleTime: 60_000,
  });

  const isSiteMapped = !!(siteDetail?.bldgId ?? stepBBldgId);
  const effectiveBldgId: number | null =
    siteDetail?.bldgId ?? stepBBldgId ?? selectedBldg?.bldgId ?? null;

  const { data: planTypes = [], isFetching: isPlansLoading } = useQuery({
    queryKey: ['floor-plan-thumbnails', effectiveBldgId],
    queryFn: () => fetchFloorPlanThumbnails(effectiveBldgId!),
    enabled: !!effectiveBldgId,
    staleTime: 5 * 60_000,
  });

  // 기존 연결 도면 자동 선택 — 변경 목적으로 진입 시 현재 도면 미리 선택
  useEffect(() => {
    if (autoSelectedRef.current || !siteDetail?.floorPlanId || planTypes.length === 0) return;
    const linked = planTypes.find((p) => p.planId === siteDetail.floorPlanId);
    if (linked) {
      setSelectedPlan(linked);
      autoSelectedRef.current = true;
    }
  }, [siteDetail?.floorPlanId, planTypes]);

  const { data: buildings = [] } = useQuery({
    queryKey: ['buildings', roadAddress],
    queryFn: () => fetchBuildings(roadAddress),
    enabled: !isSiteMapped && !!roadAddress,
    staleTime: 5 * 60_000,
  });

  const hasDanji = buildings.length > 0;

  const convertMutation = useMutation({
    mutationFn: convertFloorPlan,
    onSuccess: (planJson) => {
      setConvertedPlanJson(planJson);
      toast.success('도면 변환이 완료되었습니다.');
    },
    onError: () => {
      toast.error('도면 변환에 실패했습니다. 다른 이미지를 시도해 주세요.');
      setUploadPreviewUrl(null);
    },
  });

  const saveFloorPlanMutation = useMutation({
    mutationFn: saveFloorPlan,
    onError: () => toast.error('도면 저장에 실패했습니다.'),
  });

  const updateSiteMutation = useMutation({
    mutationFn: (payload: Parameters<typeof updateSite>[1]) => updateSite(siteId, payload),
    onSuccess: () => {
      toast.success('도면이 연결되었습니다.');
      onComplete(siteId);
    },
    onError: () => toast.error('저장에 실패했습니다.'),
  });

  const updateSiteSizeMutation = useMutation({
    mutationFn: (payload: Parameters<typeof updateSiteSize>[1]) => updateSiteSize(siteId, payload),
    onError: () => toast.error('면적 정보 업데이트에 실패했습니다.'),
  });

  const createBuildingMutation = useMutation({
    mutationFn: createBuilding,
    onError: () => toast.error('건물 정보 저장에 실패했습니다.'),
  });

  function handleFiles(files: File[]) {
    const file = files[0];
    if (!file) return;
    setSelectedPlan(null);
    setUploadPreviewUrl(URL.createObjectURL(file));
    convertMutation.mutate(file);
  }

  async function handleSave() {
    let finalBldgId = effectiveBldgId;

    // 단지 미매핑 — 평형 처리
    if (!isSiteMapped) {
      if (selectedBldg) {
        // 칩 선택 경로
        const { pyeong, squareMeters } = parseSizeLabel(selectedBldg.sizeLabel);
        try {
          await updateSiteSizeMutation.mutateAsync({
            bldgId: selectedBldg.bldgId,
            sitePyeong: pyeong ?? undefined,
            squareMeters: squareMeters ?? undefined,
          });
          finalBldgId = selectedBldg.bldgId;
        } catch { return; }
      } else if (pyeongValue) {
        // 직접 입력 경로 — 신규 단지 마스터 생성
        const v = parseInt(pyeongValue, 10);
        let finalPyeong: number | undefined;
        let finalSqm: number | undefined;
        if (!isNaN(v) && v > 0) {
          if (isPyeong) { finalPyeong = v; finalSqm = Math.round(v * SQ_PER_PYEONG); }
          else { finalSqm = v; finalPyeong = Math.round(v / SQ_PER_PYEONG); }
        }
        if (finalPyeong !== undefined || finalSqm !== undefined) {
          try {
            const { bldgId: newBldgId } = await createBuildingMutation.mutateAsync({
              bldgName: stepBResult.bldgName ?? undefined,
              roadAddress,
              bldgPyeong: finalPyeong,
              squareMeters: finalSqm,
            });
            await updateSiteSizeMutation.mutateAsync({
              bldgId: newBldgId,
              sitePyeong: finalPyeong,
              squareMeters: finalSqm,
            });
            finalBldgId = newBldgId;
          } catch { return; }
        }
      }
    }

    // 기존 타입 선택 → floorPlanId 연결
    if (selectedPlan) {
      updateSiteMutation.mutate({ floorPlanId: selectedPlan.planId });
      return;
    }

    // 업로드 도면 저장
    if (convertedPlanJson !== null) {
      if (!finalBldgId) {
        toast.error('평형 정보가 없어 도면을 저장할 수 없습니다. 평형을 선택해 주세요.');
        return;
      }
      try {
        const { planId } = await saveFloorPlanMutation.mutateAsync({
          bldgId: finalBldgId!,
          siteId,
          planJson: convertedPlanJson,
        });
        updateSiteMutation.mutate({ floorPlanId: planId });
      } catch { /* handled in onError */ }
      return;
    }

    // 도면 없이 완료
    onComplete(siteId);
  }

  const isSaving =
    updateSiteMutation.isPending ||
    saveFloorPlanMutation.isPending ||
    updateSiteSizeMutation.isPending ||
    createBuildingMutation.isPending;
  const isConverting = convertMutation.isPending;

  if (isSiteLoading) {
    return (
      <div className="space-y-3 py-4">
        <Skeleton height={40} />
        <Skeleton height={96} />
      </div>
    );
  }

  return (
    <div className="space-y-4">

      {/* 평형 필드 (필수) — E-5: 단지 매핑 유무에 따라 분기 */}
      {/* 칩·직접입력 두 모드라 라벨을 Input 밖에 두고 .field 구조를 직접 만든다(.req 는 .field > label 하위 규칙) */}
      <div className="field field-auto">
        <label htmlFor="stepC-pyeong">
          평형 정보 <span className="req" aria-hidden="true">*</span>
        </label>

        {isSiteMapped ? (
          /* 단지 매핑 완료 → 읽기전용 */
          <div>
            <StatusHint tone="success">
              {siteDetail?.sitePyeong
                ? `${siteDetail.sitePyeong}평`
                : stepBResult.sitePyeong
                  ? `${stepBResult.sitePyeong}평`
                  : '평형 정보 없음'}
            </StatusHint>
          </div>
        ) : hasDanji && !pyeongDirectInput ? (
          /* 단지 미매핑 + buildings 있음 → 칩 (StepB 동일 디자인) */
          <div className="mt-2 flex flex-wrap gap-2">
            {buildings.map((b) => {
              const isSelected = selectedBldg?.bldgId === b.bldgId;
              return (
                <FilterChip
                  key={b.bldgId}
                  active={isSelected}
                  onClick={() => {
                    setSelectedBldg(isSelected ? null : b);
                    setSelectedPlan(null);
                  }}
                >
                  {b.sizeLabel ?? '평형 미상'}
                </FilterChip>
              );
            })}
            <FilterChip
              onClick={() => { setPyeongDirectInput(true); setSelectedBldg(null); setPyeongValue(''); }}
            >
              직접 입력
            </FilterChip>
          </div>
        ) : (
          /* 단지 미매핑 + buildings 없음 OR 직접 입력 모드 */
          <div className="mt-2 space-y-2">
            <div className="flex items-center gap-2">
              <Input
                id="stepC-pyeong"
                type="number"
                value={pyeongValue}
                onChange={(e) => setPyeongValue(e.target.value)}
                placeholder="숫자 입력"
                width="fill"
              />
              {/* 평 / ㎡ 토글 */}
              <Segmented
                options={[{ label: '평', value: 'pyeong' }, { label: '㎡', value: 'sqm' }]}
                value={isPyeong ? 'pyeong' : 'sqm'}
                onChange={(v) => setIsPyeong(v === 'pyeong')}
              />
            </div>
            {pyeongValue && !isNaN(parseInt(pyeongValue, 10)) && parseInt(pyeongValue, 10) > 0 && (
              <StatusHint tone="muted">
                {isPyeong
                  ? `≈ ${Math.round(parseInt(pyeongValue, 10) * SQ_PER_PYEONG)}㎡`
                  : `≈ ${Math.round(parseInt(pyeongValue, 10) / SQ_PER_PYEONG)}평`}
              </StatusHint>
            )}
            {hasDanji && (
              <Button
                variant="ghost"
                size="sm"
                type="button"
                onClick={() => { setPyeongDirectInput(false); setPyeongValue(''); }}
              >
                ← 목록에서 선택
              </Button>
            )}
          </div>
        )}
      </div>

      {/* 타입(형) 목록 — E-5: effectiveBldgId 기준 도면 타입 조회 */}
      {effectiveBldgId && (
        <div className="rounded-xxl border border-border-default bg-surface-subtle p-4">
          {isSiteMapped && (
            <div className="mb-3">
              <StatusHint tone="success">단지 도면 자동 불러오기 완료</StatusHint>
            </div>
          )}

          {isPlansLoading ? (
            <div className="flex gap-3">
              {[0, 1, 2].map(i => <Skeleton key={i} width={96} height={104} />)}
            </div>
          ) : planTypes.length > 0 ? (
            <>
              <p className="mb-2 text-caption text-text-secondary">타입(형)을 선택하세요</p>
              <div className="flex flex-wrap gap-3">
                {planTypes.map((plan) => {
                  const isSelected = selectedPlan?.planId === plan.planId;
                  const isCurrentPlan = siteDetail?.floorPlanId === plan.planId;
                  return (
                    // 도면 썸네일 선택 타일 — ui-core 에 이미지 선택 타일 프리미티브가 없어
                    // 네이티브 button 을 그대로 쓴다(키보드·포커스가 네이티브로 보장된다).
                    // 색은 콘텐츠 영역이므로 groupTheme 를 따른다(D-07 §3.2 #18).
                    <button
                      key={plan.planId}
                      type="button"
                      onClick={() => setSelectedPlan(isSelected ? null : plan)}
                      className={`flex w-24 flex-col overflow-hidden rounded-xxl border transition ${
                        isSelected
                          ? 'border-group ring-1 ring-group'
                          : isCurrentPlan
                            ? 'border-success ring-1 ring-success'
                            : 'border-border-default hover:border-group'
                      }`}
                    >
                      {/* 썸네일 영역 — API 클라이언트 경유 blob 조회 */}
                      <div className="relative flex h-20 w-full items-center justify-center bg-surface-subtle">
                        {plan.thumbnailUrl ? (
                          <ThumbnailImage
                            thumbnailUrl={plan.thumbnailUrl}
                            alt={`${plan.sizeType}형 도면`}
                            className="h-full w-full object-contain"
                          />
                        ) : (
                          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" className="text-text-disabled">
                            <rect x="3" y="3" width="18" height="18" rx="2"/>
                            <path d="M3 9h18M9 21V9M3 15h6"/>
                          </svg>
                        )}
                        {isCurrentPlan && (
                          <span className="absolute left-1 top-1">
                            <Badge status="success">현재</Badge>
                          </span>
                        )}
                      </div>
                      {/* 타입 라벨 */}
                      <div className={`px-2 py-1.5 text-center text-caption font-semibold ${
                        isSelected ? 'bg-group text-group-onPrimary' : 'bg-surface-raised text-text-secondary'
                      }`}>
                        {plan.sizeType}형
                      </div>
                    </button>
                  );
                })}
              </div>
            </>
          ) : (
            <StatusHint tone="muted">이 단지에 등록된 도면이 없습니다.</StatusHint>
          )}
        </div>
      )}

      {/* 도면 직접 업로드 — E-5: 타입 선택 시 비활성화 */}
      <div>
        {uploadPreviewUrl && (
          <div className="mb-2 overflow-hidden rounded-xxl border border-border-default">
            <img src={uploadPreviewUrl} alt="업로드 도면 미리보기" className="h-36 w-full object-contain" />
            <div className="bg-surface-subtle px-3 py-1.5 text-center">
              <StatusHint tone={isConverting ? 'muted' : 'success'}>
                {isConverting ? '도면 변환 중…' : '변환 완료'}
              </StatusHint>
            </div>
          </div>
        )}
        {/* 숨김 input + 버튼 트리거는 FileInput 이 갖는다 (D-07 §3.2 #6) */}
        <FileInput
          accept="image/*"
          variant="secondary"
          disabled={isConverting || !!selectedPlan}
          onFiles={handleFiles}
          className="[&>button]:w-full"
        >
          {selectedPlan ? '타입 선택 시 업로드 비활성화' : '도면 직접 업로드 (이미지)'}
        </FileInput>
      </div>

      {/* 도면 없이 진행하기 */}
      <Button variant="ghost" size="sm" type="button" className="w-full" onClick={() => onComplete(siteId)}>
        도면 없이 진행하기
      </Button>

      {/* 하단 버튼 — [이전]은 주 액션(채움) 옆의 보조 강조라 secondary (D-09 §15.1) */}
      <div className="flex gap-2 pt-1">
        <Button variant="secondary" size="lg" type="button" onClick={onPrev} className="flex-none">
          ← 이전
        </Button>
        <Button
          variant="primary"
          size="lg"
          type="button"
          onClick={() => void handleSave()}
          disabled={isSaving || isConverting}
          className="flex-1"
        >
          {isSaving ? '저장 중…' : '저장'}
        </Button>
      </div>
    </div>
  );
}
